import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';

import LineChart from './components/LineChart';
import DonutChart from './components/DonutChart';
import UtilizationChart from './components/UtilizationChart';
import BaselineChart from '../../../home/Machines/MachineDetails/Health/components/BaselineChart';
import TrendChart from '../../../home/Machines/MachineDetails/MachineCharts/components/TrendChart';
import WaveformChart from '../../../home/Machines/MachineDetails/MachineCharts/components/WaveformChart';
import SpectrumChart from '../../../home/Machines/MachineDetails/MachineCharts/components/SpectrumChart';
import FeatureChart from '../../../home/Machines/MachineDetails/MachineCharts/components/FeatureChart';
import OverallAlarmChart from '../../../home/Machines/MachineDetails/MachineCharts/components/OverallAlarmChart';
import MultilineChart from '../../../home/Machines/MachineDetails/MachineCharts/components/MultilineChart';
import SimpleLineContext from './components/SimpleLineContext';
import { AnnotatedChart } from '../../../home/Annotations';
import { getVisible } from '../../../home/Annotations/selectors/annotations.selectors';

const Container = styled.div`
  ${props => props.topPadding && 'padding-top: 20px;'} padding-left: 55px;
  padding-bottom: ${props => props.noBottomPadding ? '0' : '16px'};
  ${props =>
    props.noPadding && `
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0;
    padding-right: 0;
  `}
  ${props =>
    props.noLabel && `
    padding-left: 30px;
  `}
  flex-grow: 1;
  svg:not(:root) {
    overflow: visible;
  }
  g line {
    stroke: lightgray;
    stroke-width: 1px;
  }
  ${props => props.noTopPadding && 'padding-top: 0;'}
`;

const Chart = ({ type, features, noLabel, annotationsVisible, disableAnnotations, ...rest }) => {
  // Helper function to wrap TrendChart with annotations
  const renderTrendChart = (chartType, props) => {
    // Always enable annotations on Trend charts when a tag is present
    // (covers main Trend page and embedded Trend in Spectrum/Waveform/Combined/Cepstrum)
    // Unless annotations are explicitly disabled (e.g., on machine charts page)
    if (props.tagId && !disableAnnotations) {
      // Extract time range from data
      let timeRange = null;
      try {
        // Prefer explicit domain when provided by charts reducers (moment objects allowed)
        if (props.domain && Array.isArray(props.domain) && props.domain.length === 2) {
          const normalizeToDate = (v) => {
            if (!v) return new Date(NaN);
            // Support moment-like objects { _isAMomentObject: true }
            if (v && v.isValid && typeof v.toDate === 'function') return v.toDate();
            return new Date(v);
          };
          const start = normalizeToDate(props.domain[0]);
          const end = normalizeToDate(props.domain[1]);
          if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
            timeRange = { start, end };
          }
        }

        if (!timeRange && props.data && props.data.length > 0) {
          // Normalize into a flat array of points (trend_data is array of objects with x timestamp)
          const seriesOrPoints = Array.isArray(props.data[0]) ? props.data[0] : props.data;
          const points = seriesOrPoints;

          const extractDateFromPoint = (point) => {
            if (point && typeof point === 'object' && !Array.isArray(point)) {
              const ts = point.x || point.timestamp || point.time || point.date;
              return new Date(ts);
            }
            if (Array.isArray(point)) {
              const candidate = (typeof point[0] === 'number' || typeof point[0] === 'string') ? point[0] : point[1];
              if (typeof candidate === 'number') {
                const ms = candidate < 1e12 ? candidate * 1000 : candidate;
                return new Date(ms);
              }
              return new Date(candidate);
            }
            return new Date(NaN);
          };

          const timePoints = points
            .map(extractDateFromPoint)
            .filter(date => !Number.isNaN(date.getTime()));

          if (timePoints.length > 0) {
            timeRange = {
              start: new Date(Math.min(...timePoints.map(d => d.getTime()))),
              end: new Date(Math.max(...timePoints.map(d => d.getTime())))
            };
          }
        }
      } catch (error) {
        // no-op
      }

      return (
        <AnnotatedChart
          entityType="tag"
          entityId={props.tagId}
          timeRange={timeRange}
          overlayMarkers={false}
          overlayDottedLines={false}
        >
          <TrendChart type={chartType} {...props} />
        </AnnotatedChart>
      );
    }

    return <TrendChart type={chartType} {...props} />;
  };

  return (
    <Container
      className={`chartContainer ${type}`}
      noPadding={type === 'donut'}
      noTopPadding={type === 'overallAlarm'}
      noBottomPadding={rest.miniMode}
      noLabel={noLabel}
      topPadding={
        (features && features.items && features.items.length > 0) || (features && features.length > 0)
      }
    >
      {type === 'line' && <LineChart type={type} {...rest} />}
      {type === 'multiline' && <MultilineChart type={type} {...rest} />}
      {(type === 'spectrum' || type === 'demod_spectrum') && <SpectrumChart type={type} features={features} {...rest} />}
      {type === 'feature' && <FeatureChart type={type} features={features} {...rest} />}
      {type === 'overallAlarm' && <OverallAlarmChart {...rest} type={type} />}
      {type === 'waveform' && <WaveformChart type={type} {...rest} />}
      {(type === 'trend' || type === 'demod_trend') && renderTrendChart(type, rest)}
      {type === 'baseline' && <BaselineChart type={type} {...rest} />}
      {type === 'utilization' && <UtilizationChart type={type} {...rest} />}
      {type === 'donut' && <DonutChart type={type} {...rest} />}
      {type === 'context' && <SimpleLineContext type={type} {...rest} />}
    </Container>
  );
};

Chart.propTypes = {
  type: PropTypes.string.isRequired,
  annotationsVisible: PropTypes.bool,
  disableAnnotations: PropTypes.bool
};

const mapStateToProps = state => ({
  hierarchyViewPane: state.hierarchyViewPane,
  annotationsVisible: getVisible(state)
});

Chart.defaultProps = {
  annotationsVisible: true,
  disableAnnotations: false
};

Chart.displayName = 'Chart';

export default connect(mapStateToProps, {})(withTheme(Chart));
